import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "@/utils/appToast";
import { Avatar } from "@mui/material";
import ContentContainer from "@/components/containers/contentContainer";
import { isAuthenticated } from "@/shared/services/credentialsService";
import { trackUserAction } from "@/utils/analytics";
import {
  clearRequestQuoteDraft,
  readRequestQuoteDraft,
  type RequestQuoteDraft,
} from "@/utils/requestQuoteDraft";
import { submitRequestQuoteFromDraft } from "@/utils/requestQuoteSubmit";

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 w-full">
      <p className="text-xs font-medium text-[#424654]">{label}</p>
      <p className="text-sm text-[#111c2d] whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}

export default function RequestQuoteReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.user || {});
  const [draft, setDraft] = useState<RequestQuoteDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated() || user?.type !== "user") {
      navigate("/ingresar", {
        replace: true,
        state: { from: "/request-quote/review" },
      });
      return;
    }
    const d = readRequestQuoteDraft();
    if (!d?.form?.description) {
      toast.error(t("completeQuoteFormFirst"));
      navigate("/request-quote", { replace: true });
      return;
    }
    setDraft(d);
  }, [user, navigate, t]);

  const handleConfirm = async () => {
    if (!draft || !user?._id) return;
    setSubmitting(true);
    try {
      const result = await submitRequestQuoteFromDraft({
        draft,
        userId: user._id,
        customerStripeId: user.stripeId,
      });
      if (!result.success) {
        toast.error(result.message || t("something_went_wrong"));
        return;
      }
      toast.success(t("quoteRequestSent"));
      trackUserAction("quote_request_sent", {
        provider_count:
          draft.mode === "manual" ? draft.selectedProviders.length : result.sent,
        mode: draft.mode,
        user_type: "client",
      });
      clearRequestQuoteDraft();
      navigate("/request-quotes");
    } catch (e: any) {
      toast.error(e?.message || t("something_went_wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft) {
    return (
      <ContentContainer className="!p-0 m-0 bg-[#f9f9ff] min-h-screen pt-20 xl:pt-0">
        <div className="py-16 text-center text-[#424654]">…</div>
      </ContentContainer>
    );
  }

  const { form, mode, selectedProviders } = draft;
  const serviceTypeLabel =
    [form.categoryName, form.serviceName].filter(Boolean).join(" · ") || "—";

  return (
    <ContentContainer className="!p-0 m-0 bg-[#f9f9ff] min-h-screen pb-8 pt-20 xl:pt-0">
      <div className="sticky top-20 xl:top-0 z-10 flex items-center gap-3 px-4 h-14 bg-[#f9f9ff]/95 backdrop-blur-md border-b border-[#d8e3fb]/60 shadow-sm">
        <button
          type="button"
          className="size-11 shrink-0 rounded-full flex items-center justify-center text-[#111c2d] text-lg disabled:opacity-40"
          onClick={() => navigate("/request-quote")}
          disabled={submitting}
          aria-label={t("back")}
        >
          ←
        </button>
        <h1 className="min-w-0 flex-1 text-lg sm:text-xl font-semibold text-[#111c2d] truncate">
          {t("reviewAndConfirm")}
        </h1>
      </div>

      <div className="max-w-[720px] mx-auto px-4 pt-6 pb-6 flex flex-col gap-6">
        <div className="bg-[#e7eeff] rounded-xl p-4 shadow-sm flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-full bg-[#4285F4]/10 flex items-center justify-center text-[#4285F4] text-sm font-bold">
              ✓
            </div>
            <h2 className="text-xl font-semibold text-[#111c2d]">
              {t("requestSummary")}
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            <SummaryField label={t("serviceType")} value={serviceTypeLabel} />
            <SummaryField
              label={t("location")}
              value={form.location?.address || "—"}
            />
            <SummaryField
              label={t("description")}
              value={form.description}
            />
            {form.budgetDetails ? (
              <SummaryField
                label={t("estimatedBudget")}
                value={form.budgetDetails}
              />
            ) : null}
            <SummaryField
              label={t("select_start_date_time")}
              value={
                form.startDateISO
                  ? moment(form.startDateISO).format("DD MMM YYYY, h:mm A")
                  : "—"
              }
            />
            <SummaryField label={t("urgency")} value={t(form.urgency)} />
          </div>

          {form.imagePreviewUrls?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {form.imagePreviewUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="h-16 w-24 object-cover rounded-lg border border-[#d8e3fb]"
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#111c2d]">
            {mode === "auto"
              ? t("providers")
              : `${t("selectedProfessionals")} (${selectedProviders.length})`}
          </h3>

          {mode === "auto" ? (
            <div className="bg-[#e7eeff] rounded-lg p-4 text-sm text-[#111c2d]">
              {t("autoSendNearby")}
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-1">
              {selectedProviders.map((p) => {
                const initials = (p.name || "?")
                  .split(" ")
                  .map((part) => part.charAt(0))
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <div
                    key={p.userId}
                    className="bg-[#e7eeff] rounded-lg shadow-sm w-32 shrink-0 flex flex-col items-center pt-3 pb-3 px-2"
                  >
                    {p.avatar ? (
                      <Avatar
                        src={p.avatar}
                        alt=""
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <div className="size-12 rounded-full bg-[#d8e3fb] flex items-center justify-center text-sm font-semibold text-[#4285F4]">
                        {initials}
                      </div>
                    )}
                    <p className="mt-2 text-xs font-semibold text-[#111c2d] text-center truncate w-full">
                      {p.name || p.userId}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 pt-2">
          <p className="text-sm text-[#424654] text-center">
            {t("professionalsWillRespond")}
          </p>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            className="w-full rounded-lg bg-[#4285F4] py-4 text-xs font-bold text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t("sendingQuoteRequest") : t("sendRequest")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/request-quote")}
            disabled={submitting}
            className="w-full rounded-lg border border-[#d8e3fb] bg-white py-3 text-sm font-medium text-[#4285F4] disabled:opacity-50"
          >
            {t("back")}
          </button>
        </div>
      </div>
    </ContentContainer>
  );
}
